import type { CustomArt } from "../../stores/custom-art-store";
import type { ArtData } from "./art-text";
import { renderPowerPointBackground } from "./powerpoint-background";
import {
  createPowerPointScene,
  PIXELS_PER_INCH,
  rasterizePowerPointElement,
} from "./powerpoint-scene";
import { powerPointTextLayers } from "./powerpoint-text";
import { ART_HEIGHT, ART_WIDTH } from "../../lib/schedule-art/art-canvas";

export type PowerPointExportOptions = {
  preserveTextAppearance: boolean;
  embedBackground: boolean;
  includeCustomArt: boolean;
};

export async function exportSchedulePowerPoint(
  data: ArtData,
  filename: string,
  layers: CustomArt[],
  offlineLabel: string,
  options: PowerPointExportOptions,
) {
  const { default: PptxGenJS } = await import("pptxgenjs");
  const includedArt = options.includeCustomArt ? layers : [];
  const { stage, elements } = await createPowerPointScene(
    data,
    includedArt,
    offlineLabel,
  );
  try {
    const presentation = new PptxGenJS();
    presentation.defineLayout({
      name: "SCHEDULE",
      width: ART_WIDTH / PIXELS_PER_INCH,
      height: ART_HEIGHT / PIXELS_PER_INCH,
    });
    presentation.layout = "SCHEDULE";
    presentation.title = filename.replace(/\.pptx$/, "");
    const editable = presentation.addSlide();
    const appearance = options.preserveTextAppearance
      ? presentation.addSlide()
      : undefined;
    let foreground = elements;
    if (options.embedBackground) {
      const fixed = elements.filter((element) => element.fixed);
      const background = renderPowerPointBackground(stage, fixed);
      editable.background = { data: background };
      if (appearance) appearance.background = { data: background };
      foreground = elements.filter((element) => !element.fixed);
    }
    for (const element of foreground) {
      if (element.text) {
        const textLayers = powerPointTextLayers(element.text, element.name);
        for (const textOptions of textLayers) {
          editable.addText(element.text.text(), textOptions);
        }
      }
      const needsRaster = !element.text || appearance !== undefined;
      if (!needsRaster) continue;
      const image = rasterizePowerPointElement(stage, element);
      if (!element.text) editable.addImage(image);
      if (appearance) appearance.addImage(image);
    }
    await presentation.writeFile({ fileName: filename, compression: true });
  } finally {
    stage.destroy();
  }
}
